import { Eye, Menu, Scissors, useCallback, useEffect, useRef, useState } from "./core/vendor.js";
import { AdminAuditPage, AdminCostsPage, AdminDashboardPage, AdminFrontendErrorsPage, AdminMonitorPage, AdminOnboardingPage, AdminPricingPage, AdminSalonsPage, AdminSubscriptionsPage, AdminUpdatesPage } from "./admin/admin.jsx";
import { AICallsPage, MessagesPage } from "./ai/ai.jsx";
import { LoginScreen, SalonLockGate, TermsAcceptanceGate } from "./auth/auth.jsx";
import { BookingsPage, CalendarPage } from "./calendar/calendar.jsx";
import { api } from "./core/api.js";
import { COLORS, DemoModeContext, MobileMenuContext, NavigateContext, ROLE_PAGES, SpotlightContext, UserContext, formatDate, setCalDisplayWindow, setSalonBusinessType, today, useIsMobile } from "./core/config.jsx";
import { INITIAL_APPOINTMENTS, INITIAL_CUSTOMERS, INITIAL_EMPLOYEES, INITIAL_SERVICES } from "./core/demo-data.js";
import { Btn } from "./core/ui.jsx";
import { PRODUCT_UPDATES } from "./core/updates.js";
import { CustomersPage } from "./customers/customers.jsx";
import { DashboardPage } from "./dashboard/dashboard.jsx";
import { EmployeesPage, ShiftsPage, TimeTrackingPage } from "./employees/employees.jsx";
import { AccountingPage, KassePage, PaymentsPage, PosSystemPage, TaxDashboardPage, VouchersPage } from "./finance/finance.jsx";
import { SubscriptionsPage } from "./finance/subscriptions.jsx";
import { ConfettiOverlay, FirstLoginGreeting, Sidebar, SpotlightSearch, WhatsNewModal } from "./layout/layout.jsx";
import { AGBPage, AVVPage, DSGVOPage, DatenschutzPage, HelpPage, ImpressumPage } from "./legal/legal.jsx";
import { GoogleBusinessPage, OnlineBookingPage } from "./marketing/marketing.jsx";
import { ServicesPage } from "./services/services.jsx";
import { ProfilePage, SettingsPage } from "./settings/settings.jsx";
import { PinResetGate } from "./core/ownerpin.jsx"; // OWNER_PIN_v1 -- Link aus der Mail (?pinreset=)


// ============================================================
// FUN_v1 -- Konami-Code (↑↑↓↓←→←→BA) + Konfetti bei Lifetime-Online-Buchungs-Meilensteinen
// Reine Frontend-Spaß-Features. Keine Server-Calls.
// ============================================================
const KONAMI_SEQ_v1 = ["ArrowUp","ArrowUp","ArrowDown","ArrowDown","ArrowLeft","ArrowRight","ArrowLeft","ArrowRight","b","a"];
const ONLINE_BOOKING_MILESTONES = [1, 10, 50, 100, 500, 1000, 5000];

// ============================================================
// MAIN APP (with Authentication & RBAC)
// ============================================================
function App() {
  const isMobile = useIsMobile();
  const [currentUser, setCurrentUser] = useState(null);
  // AUTH_SPLASH_v1 -- Toby 08.09.: Pull-to-Refresh auf dem iPad blitzte kurz den Anmeldebildschirm
  // auf. Ursache: currentUser ist beim Start leer und /auth/me läuft asynchron. Solange ein
  // gespeichertes Login geprüft wird, zeigen wir einen neutralen Ladezustand statt des Logins.
  // loadTokens liest nur aus dem Storage (idempotent), der Effekt unten ruft es erneut.
  const [authPending, setAuthPending] = useState(() => {
    try { return !!(api.loadTokens && api.loadTokens()); } catch (e) { return false; }
  });
  const [activePage, setActivePage] = useState("dashboard");
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMobileFab, setShowMobileFab] = useState(false);  // MOBILE_MENU_FAB_v1 (#16): fester Menue-Button beim Scrollen
  const [demoMode, setDemoMode] = useState(false);
  const [demoToast, setDemoToast] = useState(null);
  // TRIAL_READONLY_v1 -- Login-Hinweisfenster bei abgelaufenem Trial (1x pro Login)
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const expiredModalShownRef = React.useRef(false);
  // PAYMENT_DUNNING_v1 -- gelbes Zahl-Banner wegklickbar (pro Login/Session)
  const [payBannerDismissed, setPayBannerDismissed] = useState(false);
  // TDZ_FIX_v2 -- employees/services/customers/appointments State HIER deklariert (vor FUN_v1+WHATS_NEW).
  // Sonst greift unten der Konfetti-useEffect (Z. ~16147) auf `appointments` zu BEVOR es deklariert
  // ist → TDZ-ReferenceError in strict-mode JS → Blackscreen-Risiko nach Login.
  // Die ursprünglichen Decls weiter unten wurden ersetzt durch Kommentar.
  // MOCK_FLASH_FIX_v1 -- Initial-State LEER, sonst flackern 6 Demo-Termine im Kalender
  // bei Login auf (employeeId 1-4 zufällig in Natalies Salon = Belinda/Martina) und
  // verschwinden nach 1 sec wenn loadData() den echten (leeren) Stand liefert.
  // Mock-Daten kommen NUR noch zum Tragen wenn Backend offline ist (siehe loadData unten).
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [appointments, setAppointments] = useState([]);
  // ZOEY_AUG5_FIXES_v1 -- Kunden-Mapping als Helfer (Initial-Load + Auto-Refresh nutzen dasselbe)
  const mapCustomerFromApi = (c) => ({
    id: c.id, name: c.name, phone: c.phone, email: c.email, notes: c.notes, address: c.address, gender: c.gender, smsConsent: !!c.sms_consent, marketingConsent: !!c.marketing_consent, salonId: c.salon_id,
    hairType: c.hair_type, hairLength: c.hair_length, hairThickness: c.hair_thickness,
    hairCondition: c.hair_condition, birthday: c.birthday, lastVisit: c.last_visit,
    createdAt: c.created_at,
    totalSpent: c.total_spent, visits: c.visits, noShows: (c.no_show_total ?? c.no_shows), lastNoShow: c.last_no_show || null, /* NO_SHOW_MARK_v1 */
    loyaltyPoints: c.loyalty_points || 0, loyaltyTier: c.loyalty_tier || "neu",
    loyaltyRedeemed: c.loyalty_rewards_redeemed || [],
    preferredEmployee: c.preferred_employee_id, customerType: c.customer_type,
    comesRegularly: !!c.comes_regularly, regularProducts: c.regular_products || [],
    cardHistory: (c.card_history || []).map(h => ({ date: h.date, employee: h.employee_name, text: h.text })),
  });
  // ZOEY_AUG5_FIXES_v1 -- aktueller customers-Stand fuer den Auto-Refresh (ohne Effect-Neustart)
  const customersRef = useRef([]);
  customersRef.current = customers;
  // FUN_v1 -- Konami-Code (Retro-Mode 60s) + Konfetti bei Online-Buchungs-Meilensteinen
  const [retroMode, setRetroMode] = useState(false);
  const konamiBufRef = useRef([]);
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key;
      konamiBufRef.current = [...konamiBufRef.current, k].slice(-KONAMI_SEQ_v1.length);
      const buf = konamiBufRef.current;
      if (buf.length === KONAMI_SEQ_v1.length &&
          KONAMI_SEQ_v1.every((kk, i) => kk.toLowerCase() === (buf[i] || "").toLowerCase())) {
        konamiBufRef.current = [];
        setRetroMode(true);
        setTimeout(() => setRetroMode(false), 60000);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (retroMode) {
      document.documentElement.style.filter = "hue-rotate(45deg) contrast(1.2) saturate(1.4)";
      document.documentElement.style.transition = "filter 0.4s";
    } else {
      document.documentElement.style.filter = "";
    }
    return () => { document.documentElement.style.filter = ""; };
  }, [retroMode]);
  const [celebrate, setCelebrate] = useState(null);
  // MILESTONE_NO_REPLAY_v1 (Toby 05.08.) -- Konfetti feierte alte Meilensteine auf jedem
  // neuen Geraet/Browser erneut (Merker lag NUR im localStorage des Geraets; privater
  // Modus = jedes Mal). Jetzt: beim ersten Datenstand nach Login nur BASELINE merken,
  // gefeiert wird ausschliesslich ein Meilenstein, der WAEHREND der Sitzung ueberschritten
  // wird. localStorage bleibt als Zusatz-Merker, ist aber nicht mehr die einzige Bremse.
  const milestoneBaseRef = useRef({ salonId: null, count: null });
  useEffect(() => {
    if (!currentUser) return;
    // FUN_MILESTONE_ADMIN_GUARD_v1 -- Meilenstein-Konfetti nur fuer salon-gebundene Inhaber.
    // Im Admin-Modus liefert die API ALLE Salons (get_tenant_filter=None), sodass die
    // Online-Buchungen aller Salons quersummiert wuerden und faelschlich einen Meilenstein
    // (z.B. 50) ausloesen. Gilt fuer ALLE Meilensteine.
    if (currentUser.role === "admin" || !currentUser.salon_id) return;
    // DASHBOARD_TILES_v1 -- Zoey-Buchungen tragen jetzt source='zoey' (vorher 'online'),
    // zaehlen fuer die Meilensteine aber weiter als Online-Buchung.
    const onlineCount = (appointments || []).filter(a => a && (a.source === "online" || a.source === "zoey")).length;
    if (onlineCount === 0) return;
    const seenKey = "shearly_milestone_seen_" + (currentUser.salon_id || "x");
    let lastSeen = 0;
    try { lastSeen = Number(localStorage.getItem(seenKey) || 0); } catch (e) {}
    const highestReached = ONLINE_BOOKING_MILESTONES.filter(m => m <= onlineCount);
    const highest = highestReached.length > 0 ? Math.max(...highestReached) : 0;
    const base = milestoneBaseRef.current;
    if (base.salonId !== currentUser.salon_id || base.count === null) {
      // Erster Datenstand fuer diesen Salon in dieser Sitzung: Baseline setzen, NIE feiern.
      milestoneBaseRef.current = { salonId: currentUser.salon_id, count: onlineCount };
      if (highest > lastSeen) { try { localStorage.setItem(seenKey, String(highest)); } catch (e) {} }
      return;
    }
    const reached = ONLINE_BOOKING_MILESTONES.filter(m => m <= onlineCount && m > lastSeen);
    if (reached.length > 0 && onlineCount > base.count) {
      const milestone = Math.max(...reached);
      setCelebrate(milestone);
      // Baseline nachziehen: dedupliziert auch, wenn localStorage nicht schreibbar ist
      // (Safari privater Modus) -- sonst kaeme das Fenster beim naechsten Refresh wieder.
      milestoneBaseRef.current = { salonId: currentUser.salon_id, count: onlineCount };
      try { localStorage.setItem(seenKey, String(milestone)); } catch (e) {}
    }
  }, [currentUser && currentUser.user_id, currentUser && currentUser.salon_id, appointments && appointments.length]);

  // WHATS_NEW_v1 -- Update-Popup beim Login mit ungesehenen Einträgen aus PRODUCT_UPDATES
  // Hooks MÜSSEN vor early-returns (Spotlight-Lektion).
  const [unseenUpdates, setUnseenUpdates] = useState([]);
  useEffect(() => {
    if (!currentUser) return;
    if (typeof PRODUCT_UPDATES === "undefined" || !PRODUCT_UPDATES) return;
    let seen = [];
    try { seen = JSON.parse(localStorage.getItem("shearly_seen_updates_v1") || "[]"); } catch (e) {}
    const unseen = PRODUCT_UPDATES.filter(u => !seen.includes(u.id));
    setUnseenUpdates(unseen.length > 0 ? unseen.slice(0, 5) : []);
  }, [currentUser && currentUser.user_id]);
  const dismissUpdates = () => {
    try {
      const allIds = (PRODUCT_UPDATES || []).map(u => u.id);
      localStorage.setItem("shearly_seen_updates_v1", JSON.stringify(allIds));
    } catch (e) {}
    setUnseenUpdates([]);
  };

  // FIRST_LOGIN_GREETING_v1 -- Begrüßungsfenster: der SERVER entscheidet (atomarer
  // Check-and-Set pro Salon + Tag, erst ab 6:00 Uhr). Nur salon-gebundene Nutzer fragen an.
  // FIRST_LOGIN_GREETING_OFF_v1 (Toby 03.09.2026: "nimm mal die Begrüßung wenn man sich einloggt wieder raus")
  // -- Fenster ist AUS. Server-Endpunkt + Komponente bleiben stehen; zum Reaktivieren nur diese Konstante auf true.
  const FIRST_LOGIN_GREETING_ENABLED = false;
  const [showGreeting, setShowGreeting] = useState(false);
  useEffect(() => {
    if (!FIRST_LOGIN_GREETING_ENABLED) return;
    if (!currentUser || currentUser.role === "admin" || !currentUser.salon_id) return;
    let alive = true;
    api.request("/greeting/first-login", { method: "POST", body: "{}" })
      .then(r => { if (alive && r && r.ok && r.data && r.data.show === true) setShowGreeting(true); })
      .catch(() => {});
    return () => { alive = false; };
  }, [currentUser && currentUser.user_id]);

  // MAINT_NOTICE_v1 (27.09.2026) -- Hinweis auf angekündigte Server-Wartung. Text + Zeitfenster entscheidet
  // der Server (/api/system-status -> notice, Regeln in utils/maint_notice.py). Nur eine gültige Antwort
  // ändert den Zustand: Ist der Server während der Wartung weg, bleibt der Streifen stehen; läuft er
  // wieder, kommt kein Hinweis mehr und der Streifen verschwindet von selbst.
  const [sysNotice, setSysNotice] = useState(null);
  useEffect(() => {
    if (!currentUser) return;
    let alive = true;
    const load = () => {
      fetch("/api/system-status", { cache: "no-store" })
        .then(r => (r.ok ? r.json() : null))
        .then(d => {
          if (!alive || !d || typeof d !== "object") return;
          const t = d.notice && typeof d.notice.text === "string" ? d.notice.text.trim() : "";
          setSysNotice(t || null);
        })
        .catch(() => {});
    };
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    load();
    const iv = setInterval(load, 60000);
    document.addEventListener("visibilitychange", onVisible);
    return () => { alive = false; clearInterval(iv); document.removeEventListener("visibilitychange", onVisible); };
  }, [currentUser && currentUser.user_id]);

  // SPOTLIGHT_v1.1 -- State + Listener müssen vor jeder early-return-Zeile (z.B. !currentUser → LoginScreen)
  // sein, sonst Hooks-Order-Verstoß und Blackscreen nach Login.
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  // SPOTLIGHT_CRM_JUMP_v1 -- Kunde aus der Suche direkt im CRM öffnen (consume-once)
  const [crmFocusCustomerId, setCrmFocusCustomerId] = useState(null);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setSpotlightOpen(o => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // TDZ_FIX_v2: employees/services/customers/appointments wurden oben verschoben (vor FUN_v1).
  const [badgeAwards, setBadgeAwards] = useState([]);
  const [enabledFeatures, setEnabledFeatures] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const [apiMode, setApiMode] = useState(true); // true = real API, false = mock

  // Load data from API after login
  const loadData = useCallback(async (user) => {
    // SEC_BLOCK2_v1 (SEC-002) -- die Steuerberatung sieht nur ihren Bereich; Mitarbeiter, Kunden, Termine usw.
    // beantwortet der Server ihr mit 403 (und der Fehlerweg unten würde Demo-Daten einsetzen) -> gar nicht laden.
    if (user && user.role === "tax_consultant") { setDataLoaded(true); return; }
    // ADMIN_NO_APPT_POLL_v1 (04.09.2026): Admin ohne Salon hat keinen Kalender (ROLE_PAGES.admin),
    // bekam aber ALLE Termine aller Salons (6.105 Stück, 8,8 MB, ~2 s Server-Blockade) — beim Start
    // und alle 45 s im Poll unten. Für diese Sitzungen wird der Termin-Abruf übersprungen.
    // Impersonation ist unberührt: der Token trägt dann Rolle + salon_id des Salons.
    const _skipAppts = !!(user && user.role === "admin" && !user.salon_id);
    const [empRes, svcRes, custRes, apptRes, badgeRes, featRes] = await Promise.all([
      api.getEmployees(),
      api.getServices(),
      api.getCustomers(),
      _skipAppts ? Promise.resolve({ ok: true, data: [] }) : api.getAppointments(), // ADMIN_NO_APPT_POLL_v1
      api.getBadgeAwards(),
      api.getEnabledFeatures(),
    ]);

    if (empRes.ok && !empRes.offline) {
      setApiMode(true);
      // Transform API data to match frontend format
      if (empRes.ok) setEmployees(empRes.data.map(e => ({
        id: e.id, name: e.name, role: e.role_title, color: e.color, phone: e.phone, email: e.email,
        skills: e.skills || [], hourlyRate: e.hourly_rate, avatar: e.avatar,
        // EMPLOYEE_AVATAR_UPLOAD_v1 -- initial-Loader muss avatar_image_url durchreichen,
        // sonst ist das Foto nach Login/Reload weg bis reloadEmployees neu mappt.
        avatar_image_url: e.avatar_image_url || null, avatarImageUrl: e.avatar_image_url || null,
        isOwner: !!e.is_owner, isManager: !!e.is_manager, managerPages: e.manager_pages || [], commissionRate: e.commission_rate,
        onlineBookable: e.online_bookable !== 0 && e.online_bookable !== false, // EMP_ONLINE_BLOCK_v1
        kasseAccess: e.kasse_access !== 0 && e.kasse_access !== false, // KASSE_EMP_ACCESS_v1
      })));
      if (svcRes.ok) setServices(svcRes.data.map(s => ({
        id: s.id, name: s.name, category: s.category, basePrice: s.base_price,
        baseDuration: s.base_duration,
        priceModifiers: { weekend: s.price_mod_weekend, seniorStylist: s.price_mod_senior },
        durationModifiers: { longHair: s.duration_mod_long_hair, thickHair: s.duration_mod_thick_hair },
        onlineBookable: s.online_bookable !== 0 && s.online_bookable !== false, // SVC_ONLINE_TOGGLE_v1
        // PHASE_BOOKING_v1 -- Phasen fuer Einwirkzeit-Anzeige im Kalender
        phases: s.phases || [],
      })));
      // ZOEY_AUG5_FIXES_v1 -- Mapping in mapCustomerFromApi ausgelagert (Auto-Refresh nutzt es mit)
      if (custRes.ok) setCustomers(custRes.data.map(mapCustomerFromApi));
      if (apptRes.ok) setAppointments(apptRes.data.map(a => ({
        id: a.id, customerId: a.customer_id, employeeId: a.employee_id,
        serviceIds: (a.services || []).map(s => s.id),
        // CAL_CARD_SERVICE_NAME_FIX_v1 -- Namen direkt mitspeichern (für inaktive Services!)
        serviceNames: (a.services || []).map(s => s.name).filter(Boolean),
        date: a.date,
        startTime: a.start_time, totalDuration: a.total_duration, totalPrice: a.total_price,
        status: a.status, notes: a.notes, paymentStatus: a.payment_status,
        paymentMethod: a.payment_method, source: a.source, customerName: a.customer_name,
        posReceiptNumber: a.pos_receipt_number, // POS_APPOINTMENTS_v1 -- Bon-Nr. der Shearly Kasse
        // BOOKING_PREPAY_v1 -- Online-Vorkasse (SumUp): Betrag + Zustand pending|paid|expired|refunded
        prepaidAmount: a.prepaid_amount != null ? Number(a.prepaid_amount) : null,
        prepayStatus: a.prepay_status || null, prepayDueAt: a.prepay_due_at || null,
        prepayProvider: a.prepay_provider || null, // PREPAY_PAYPAL_v1 -- sumup|paypal
        prepayKind: a.prepay_kind || null, // BOOKING_PAY_ONLINE_v1 -- required|optional (NULL mit Vorkasse = required)
        prepayRefundAmount: a.prepay_refund_amount != null ? Number(a.prepay_refund_amount) : null,
        prepayRetainedAmount: a.prepay_retained_amount != null ? Number(a.prepay_retained_amount) : null, // PREPAY_LEDGER_v1 -- einbehaltene Ausfallgebuehr
        prepayRetainedRef: a.prepay_retained_ref || null, // PREPAY_LEDGER_v1 -- Bon-Nr. der Kasse oder '<id>-retained' (noch ohne Beleg)
        createdAt: a.created_at, // DASHBOARD_TILES_v1 -- fuer "Neue Online-Buchungen seit gestern"
        employeeName: a.employee_name, employeeColor: a.employee_color,
        // APPOINTMENT_PHASES_v1
        isPhaseMaster: !!a.is_phase_master, phaseParentId: a.phase_parent_id,
        phaseNo: a.phase_no, phaseBusyPct: a.phase_busy_pct, phaseLabel: a.phase_label,
        phaseKeepBusy: a.phase_keep_busy, // IDLE_PHASE_BLOCK_v1
        // APPOINTMENT_ASSISTANT_v1
        assistantEmployeeId: a.assistant_employee_id, assistantDurationMin: a.assistant_duration_min,
        // SERIES_CANCEL_v1 -- Serien-Verknüpfung mitladen (für "ganze Serie stornieren")
        recurringParentId: a.recurring_parent_id, recurringIntervalWeeks: a.recurring_interval_weeks,
      })));
      if (badgeRes.ok) setBadgeAwards(badgeRes.data || []);
      if (featRes.ok) {
        setEnabledFeatures(featRes.data || {});
        // CAL_DISPLAY_WINDOW_v1 -- Kalender-Anzeigefenster des Salons übernehmen (null = Standard 08-20)
        setCalDisplayWindow(featRes.data && featRes.data.cal_start_hour, featRes.data && featRes.data.cal_end_hour);
        // BUSINESS_TYPE_v1 -- Branche übernehmen (friseur|kosmetik)
        setSalonBusinessType((featRes.data && featRes.data.business_type) || "friseur"); // INDEX_SPLIT_B1_v1
      }
      console.log("Data loaded from API");
    } else {
      // MOCK_FLASH_FIX_v1 -- Backend offline → erst JETZT Mock-Daten setzen (für Demo-Modus)
      console.log("Using mock data (backend offline)");
      setEmployees(INITIAL_EMPLOYEES);
      setServices(INITIAL_SERVICES);
      setCustomers(INITIAL_CUSTOMERS);
      setAppointments(INITIAL_APPOINTMENTS);
    }
    setDataLoaded(true);
  }, []);

  const handleLogin = (user) => {
    setCurrentUser(user);
    if (user.role === "admin") setActivePage("admin-dashboard");
    else setActivePage("dashboard");
    // TRIAL_LOCK_v1 -- gesperrt: kein loadData (APIs antworten 403 SALON_LOCKED,
    // der Mock-Fallback würde sonst fremde Demo-Daten in den State legen)
    if (user.salon_lock && user.salon_lock.state === "locked" && user.role !== "admin") return;
    // Load real data after login
    loadData(user); // ADMIN_NO_APPT_POLL_v1: Nutzer mitgeben
  };

  // SESSION_REHYDRATE_v1 + PERSIST_LOGIN_FIX_v1 --
  // Bei Mount: Tokens IN MEMORY laden (loadTokens), dann /api/auth/me rufen.
  // Vorher: tok wurde nur aus storage gelesen aber api.token blieb null -> request ohne
  // Authorization-Header -> 401 -> Token weggeworfen -> "Merken" hat nie funktioniert.
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // KRITISCH: api.token IN MEMORY befuellen, sonst sendet api.request keinen Auth-Header
        const hasTok = api.loadTokens && api.loadTokens();
        if (!hasTok) { setAuthPending(false); return; }  // AUTH_SPLASH_v1
        const r = await api.request("/auth/me");  // korrekt: Backend-Route ist /api/auth/me
        if (!mounted) return;
        if (r && r.ok && r.data && r.data.id) {
          setCurrentUser(r.data);
          if (r.data.role === "admin") setActivePage("admin-dashboard");
          else setActivePage("dashboard");
          // TRIAL_LOCK_v1 -- gesperrt: kein loadData (siehe handleLogin)
          if (!(r.data.salon_lock && r.data.salon_lock.state === "locked" && r.data.role !== "admin")) {
            loadData(r.data); // ADMIN_NO_APPT_POLL_v1: Nutzer mitgeben
          }
        } else if (r && r.status === 401) {
          // Token explizit abgelehnt -> wegschmeißen damit Login-Screen sauber ist
          api.clearTokens && api.clearTokens();
        }
        // Bei Network-Errors / 5xx: Token NICHT loeschen, User kann's nochmal probieren
      } catch (e) {}
      finally { if (mounted) setAuthPending(false); }  // AUTH_SPLASH_v1: Splash in jedem Fall beenden
    })();
    return () => { mounted = false; };
  }, []);  // einmalig bei App-Mount

  // TRIAL_LOCK_v1 -- Rückkehr von Stripe (?payment=success) im NICHT-gesperrten Zustand
  // (Warn-/Kulanz-Banner): /api/stripe/activate stellt den Plan um (kein Webhook vorhanden),
  // danach /auth/me neu laden, damit Banner sofort verschwindet. Im gesperrten Zustand
  // übernimmt SalonLockGate denselben Ablauf.
  useEffect(() => {
    if (!currentUser || currentUser.role !== "owner" || currentUser.isDemo) return;
    if (currentUser.salon_lock && currentUser.salon_lock.state === "locked") return;
    let search = "";
    try { search = window.location.search || ""; } catch (e) { return; }
    if (search.indexOf("payment=success") === -1) return;
    try { window.history.replaceState({}, "", window.location.pathname); } catch (e) {}
    (async () => {
      try {
        await api.request("/stripe/activate", { method: "POST", body: "{}" });
        const me = await api.request("/auth/me");
        if (me && me.ok && me.data && me.data.id) setCurrentUser(me.data);
      } catch (e) {}
    })();
  }, [currentUser && currentUser.id]);

  // CALENDAR_AUTOREFRESH_v1 -- Polling fuer Appointments alle 45s.
  // Team sitzt morgens bis abends in Shearly. Wenn Kunden ueber Online-Buchung
  // / Zoey / KI-Tele neue Termine erstellen, muessen die OHNE manuellen Reload
  // im Kalender erscheinen. Polling pausiert wenn der Tab inaktiv ist (sparen).
  React.useEffect(() => {
    if (!currentUser) return;
    // ADMIN_NO_APPT_POLL_v1: Admin ohne Salon pollt keine Termine (kein Kalender, 8,8 MB alle 45 s).
    if (currentUser.role === "admin" && !currentUser.salon_id) return;
    if (currentUser.role === "tax_consultant") return;  // SEC_BLOCK2_v1 (SEC-002) -- kein Termin-Abruf für die Kanzlei
    let alive = true;
    const refresh = async () => {
      if (!alive) return;
      // Pausiere wenn Tab inaktiv (Browser-Tab im Hintergrund)
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      try {
        const r = await api.getAppointments();
        if (!alive || !r || !r.ok || !Array.isArray(r.data)) return;
        // ZOEY_AUG5_FIXES_v1 -- bringt eine neue Buchung (Zoey/Online/KI-Tele) einen Kunden
        // mit, der noch nicht im lokalen customers-State ist, Kundenliste nachladen.
        // Vorher zeigte der Kalender bis zum Hard-Reload "Kunde" statt des echten Namens.
        try {
          const _known = new Set((customersRef.current || []).map(c => c.id));
          if (r.data.some(a => a.customer_id && !_known.has(a.customer_id))) {
            api.getCustomers().then(cr => {
              if (alive && cr && cr.ok && Array.isArray(cr.data)) setCustomers(cr.data.map(mapCustomerFromApi));
            });
          }
        } catch (e) { /* silent — Anzeige-Fallback apt.customerName greift trotzdem */ }
        // Bestehende lokale optimistische Mutationen NICHT ueberschreiben.
        // Daher: nur _neue_ ids einmischen + entfernte droppen.
        setAppointments(prev => {
          const serverIds = new Set(r.data.map(a => a.id));
          const mapped = r.data.map(a => ({
            id: a.id, customerId: a.customer_id, employeeId: a.employee_id,
            serviceIds: (a.services || []).map(s => s.id),
            serviceNames: (a.services || []).map(s => s.name).filter(Boolean),
            date: a.date,
            startTime: a.start_time, totalDuration: a.total_duration, totalPrice: a.total_price,
            status: a.status, notes: a.notes, paymentStatus: a.payment_status,
            paymentMethod: a.payment_method, source: a.source, customerName: a.customer_name,
        posReceiptNumber: a.pos_receipt_number, // POS_APPOINTMENTS_v1 -- Bon-Nr. der Shearly Kasse
        // BOOKING_PREPAY_v1 -- Online-Vorkasse (SumUp): Betrag + Zustand pending|paid|expired|refunded
        prepaidAmount: a.prepaid_amount != null ? Number(a.prepaid_amount) : null,
        prepayStatus: a.prepay_status || null, prepayDueAt: a.prepay_due_at || null,
        prepayProvider: a.prepay_provider || null, // PREPAY_PAYPAL_v1 -- sumup|paypal
        prepayKind: a.prepay_kind || null, // BOOKING_PAY_ONLINE_v1 -- required|optional (NULL mit Vorkasse = required)
        prepayRefundAmount: a.prepay_refund_amount != null ? Number(a.prepay_refund_amount) : null,
        prepayRetainedAmount: a.prepay_retained_amount != null ? Number(a.prepay_retained_amount) : null, // PREPAY_LEDGER_v1 -- einbehaltene Ausfallgebuehr
        prepayRetainedRef: a.prepay_retained_ref || null, // PREPAY_LEDGER_v1 -- Bon-Nr. der Kasse oder '<id>-retained' (noch ohne Beleg)
            createdAt: a.created_at, // DASHBOARD_TILES_v1 -- fuer "Neue Online-Buchungen seit gestern"
            // APPOINTMENT_PHASES_v1
            isPhaseMaster: !!a.is_phase_master, phaseParentId: a.phase_parent_id,
            phaseNo: a.phase_no, phaseBusyPct: a.phase_busy_pct, phaseLabel: a.phase_label,
            phaseKeepBusy: a.phase_keep_busy, // IDLE_PHASE_BLOCK_v1
            // APPOINTMENT_ASSISTANT_v1
            assistantEmployeeId: a.assistant_employee_id, assistantDurationMin: a.assistant_duration_min,
        // SERIES_CANCEL_v1 -- Serien-Verknüpfung mitladen (für "ganze Serie stornieren")
        recurringParentId: a.recurring_parent_id, recurringIntervalWeeks: a.recurring_interval_weeks,
            employeeName: a.employee_name, employeeColor: a.employee_color,
          }));
          // Keep optimistic ones (negative ids) if any
          const localOnly = prev.filter(p => typeof p.id === "number" && p.id < 0);
          return [...mapped, ...localOnly];
        });
      } catch (e) { /* silent */ }
    };
    const intervalMs = 45000; // 45s — Balance aus Frische und Server-Last
    const t = setInterval(refresh, intervalMs);
    // Sofort beim Tab-Wechsel re-fetch (User kommt aus Hintergrund zurueck)
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    // NOTIF_DEEP_LINK_v1 -- externe Trigger fuer sofortigen Refresh
    // (z.B. nach Klick auf new_booking-Notification, damit der frische Termin
    // sofort im Kalender vorhanden ist bevor das Pulse-Highlight greift).
    const onExt = () => { refresh(); };
    window.addEventListener("shearly-refresh-appts", onExt);
    return () => {
      alive = false; clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("shearly-refresh-appts", onExt);
    };
  }, [currentUser]);

  // ---- Demo Mode ----
  const showDemoToast = (msg) => {
    setDemoToast(msg || "Demo-Modus — Änderungen nicht möglich");
    setTimeout(() => setDemoToast(null), 2500);
  };

  // TRIAL_READONLY_v1 -- abgelaufener Trial: der Inhaber bekommt sein System im
  // NUR-LESE-Modus (alles sichtbar, keine Änderungen) statt des roten Vollbild-
  // Sperrschirms. Gilt nur für Inhaber (Mitarbeiter -> SalonLockGate wie bisher)
  // und nur bei Trial-Ablauf (admin-pausiert bleibt hart gesperrt).
  // PAYMENT_DUNNING_v1 -- gleicher Lese-Modus auch bei Zahlungsverzug (reason='payment_overdue').
  const readOnly = !!(currentUser && currentUser.salon_lock
    && currentUser.salon_lock.state === "locked"
    && (currentUser.salon_lock.reason === "trial_expired" || currentUser.salon_lock.reason === "payment_overdue")
    && currentUser.role === "owner" && !currentUser.isDemo);
  const showReadOnlyToast = () => {
    const _ro = (currentUser && currentUser.salon_lock && currentUser.salon_lock.reason) || "";
    setDemoToast(_ro === "payment_overdue"
      ? "Zahlung offen — im Lese-Modus sind keine Änderungen möglich. Bitte Zahlung im Abo-Bereich klären."
      : "Testzeitraum abgelaufen — im Lese-Modus sind keine Änderungen möglich. Bitte Abo aktivieren.");
    setTimeout(() => setDemoToast(null), 3200);
  };
  // Schreib-Aufrufe sind serverseitig bereits geblockt (TRIAL_READONLY_v1, server.py);
  // hier zusätzlich im Frontend abfangen (gleiches Muster wie der Demo-Modus),
  // damit Schreib-Versuche gar nicht erst beim Server landen und keine Fehler aufpoppen.
  // Auth-/Stripe-Pfade (Bezahlweg + Freischaltung) bleiben frei.
  React.useEffect(() => {
    if (!readOnly) return;
    if (!api._roOriginalRequest) {
      api._roOriginalRequest = api.request.bind(api);
      api.request = function(path, options = {}) {
        const method = (options.method || "GET").toUpperCase();
        const p = path || "";
        const payPath = p.indexOf("/auth/") === 0 || p.indexOf("/stripe/") === 0;
        if (["POST", "PUT", "DELETE", "PATCH"].includes(method) && !payPath) {
          showReadOnlyToast();
          return Promise.resolve({ ok: false, status: 403, data: { error: "read_only", code: "SALON_LOCKED_RO" }, readOnly: true });
        }
        return api._roOriginalRequest(path, options);
      };
    }
    return () => {
      if (api._roOriginalRequest) { api.request = api._roOriginalRequest; api._roOriginalRequest = null; }
    };
  }, [readOnly]);
  // Hinweisfenster genau 1x pro Login zeigen
  React.useEffect(() => {
    if (readOnly && !expiredModalShownRef.current) {
      expiredModalShownRef.current = true;
      setShowExpiredModal(true);
    }
  }, [readOnly]);

  const handleDemoLogin = () => {
    const demoUser = {
      id: 999, email: "demo@shearly.de", name: "Demo Inhaber",
      role: "owner", salonId: 1, isDemo: true,
    };
    // Intercept API mutations — block POST/PUT/DELETE in demo
    if (!api._originalRequest) {
      api._originalRequest = api.request.bind(api);
      api.request = function(path, options = {}) {
        const method = (options.method || "GET").toUpperCase();
        if (["POST", "PUT", "DELETE", "PATCH"].includes(method)) {
          console.log("[Demo] Blocked", method, path);
          showDemoToast();
          return Promise.resolve({ ok: false, status: 403, data: { error: "Demo-Modus" }, demo: true });
        }
        return api._originalRequest(path, options);
      };
    }
    setDemoMode(true);
    setCurrentUser(demoUser);
    setActivePage("dashboard");
    // Keep INITIAL_* mock data — no API call needed
    setDataLoaded(true);
    console.log("[Shearly] Demo mode activated");
  };

  // In demo mode, wrap setters to block mutations
  const guardedSetEmployees = demoMode ? () => showDemoToast() : (readOnly ? () => showReadOnlyToast() : setEmployees);
  const guardedSetServices = demoMode ? () => showDemoToast() : (readOnly ? () => showReadOnlyToast() : setServices);
  const guardedSetCustomers = demoMode ? () => showDemoToast() : (readOnly ? () => showReadOnlyToast() : setCustomers);
  const guardedSetAppointments = demoMode ? () => showDemoToast() : (readOnly ? () => showReadOnlyToast() : setAppointments);

  // MODAL_SCROLL_TRAP_v1 -- iPad: Wisch-Geste ueber einem offenen Fenster (Overlay)
  // scrollte den Hintergrund (<main>), weil die Overlays inline im Content-Baum haengen
  // und der Browser die Geste dorthin weiterreicht ("scroll chaining"). Ein globaler
  // Fänger statt 14 Einzel-Fixes: Beginnt eine Scroll-Geste in einem Vollbild-Overlay
  // und liegt zwischen Fingern und Overlay KEIN eigener scrollbarer Bereich (z.B. die
  // Fensterliste selbst), wird die Geste geschluckt. Scrollen IM Fenster bleibt normal.
  useEffect(() => {
    const isOverlay = (el) => {
      const s = el.style;
      if (!s || s.position !== "fixed") return false;
      return s.inset === "0px" || (s.top === "0px" && s.right === "0px" && s.bottom === "0px" && s.left === "0px");
    };
    const canScroll = (el) => {
      if (el.scrollHeight <= el.clientHeight + 1 && el.scrollWidth <= el.clientWidth + 1) return false;
      const cs = getComputedStyle(el);
      return /(auto|scroll)/.test(cs.overflowY + cs.overflowX);
    };
    const trap = (e) => {
      let el = e.target;
      // Formularfelder nie anfassen (eigene Gesten, z.B. Textcursor ziehen)
      if (el && el.closest && el.closest("input, textarea, select, [contenteditable]")) return;
      let scrollableBetween = false;
      while (el && el !== document.body && el.nodeType === 1) {
        if (canScroll(el)) scrollableBetween = true;
        if (isOverlay(el)) {
          if (!scrollableBetween && e.cancelable) { try { e.preventDefault(); } catch (_) {} }
          return;
        }
        el = el.parentElement;
      }
    };
    document.addEventListener("touchmove", trap, { passive: false });
    document.addEventListener("wheel", trap, { passive: false });
    return () => {
      document.removeEventListener("touchmove", trap);
      document.removeEventListener("wheel", trap);
    };
  }, []);

  const handleLogout = () => {
    // Restore original API if demo mode was active
    if (api._originalRequest) {
      api.request = api._originalRequest;
      delete api._originalRequest;
    }
    // TRIAL_READONLY_v1 -- ebenso den Lese-Modus-Interceptor zuruecksetzen
    if (api._roOriginalRequest) {
      api.request = api._roOriginalRequest;
      api._roOriginalRequest = null;
    }
    try { api.logout && api.logout(); } catch (e) {}  // SEC_BLOCK2_v1 -- Sitzung am Server beenden (vor clearTokens)
    api.clearTokens();
    setCurrentUser(null);
    setActivePage("dashboard");
    setApiMode(false);
    setDataLoaded(false);
    setDemoMode(false);
  };

  // AUTH_SPLASH_v1 -- gespeichertes Login wird noch geprüft: neutraler Ladezustand in App-Farben,
  // kein Anmeldebildschirm (der blitzte beim Reload/Pull-to-Refresh kurz auf).
  if (!currentUser && authPending) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)", color: "var(--text)" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <Scissors size={28} color={COLORS.primary} />
          <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.primary, letterSpacing: 0.2 }}>Shearly</div>
          <div style={{ width: 22, height: 22, borderRadius: "50%", border: "2px solid var(--border)", borderTopColor: COLORS.primary, animation: "spin 0.8s linear infinite", marginTop: 4 }} />
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!currentUser) {
    return <LoginScreen onLogin={handleLogin} onDemoLogin={handleDemoLogin} />;
  }

  // TERMS_ACCEPTANCE_v1 -- Click-Wrap-Modal blockt App bis akzeptiert
  if (currentUser.terms_acceptance_required && currentUser.role !== "tax_consultant") {  // TAX_CONSULTANT_NO_TERMS_v1
    return <TermsAcceptanceGate currentUser={currentUser} onAccepted={(updated) => setCurrentUser(updated)} onLogout={handleLogout} />;
  }

  // TRIAL_LOCK_v1 -- sanfte Sperre: Inhaber sieht nur noch die Abo-Seite,
  // Mitarbeiter einen Hinweis. Admin (Toby) ist nie betroffen.
  // TRIAL_READONLY_v1 -- Ausnahme: abgelaufener Trial + Inhaber bekommt NICHT den
  // roten Vollbild-Schirm, sondern das normale System im Lese-Modus (readOnly).
  // Nur direkt nach Rückkehr von Stripe (?payment=success) weiter SalonLockGate,
  // weil dessen Bestätigungs-/Freischalt-Ablauf den Salon automatisch entsperrt.
  if (currentUser.salon_lock && currentUser.salon_lock.state === "locked" && currentUser.role !== "admin" && !currentUser.isDemo) {
    const _returningFromPayment = (() => { try { return window.location.search.indexOf("payment=success") !== -1; } catch (e) { return false; } })();
    if (!readOnly || _returningFromPayment) {
      return <SalonLockGate currentUser={currentUser} onLogout={handleLogout}
        onUnlocked={(updated) => { setCurrentUser(updated); loadData(); }} />;
    }
  }

  // Filter pages based on role
  // RECEPTION_NO_FINANCIALS_v1 + SALONLTG_NO_FINANCIALS_v1 -- nur Inhaber/Admin sehen Salon-Zahlen im Dashboard; Empfang UND Salonleitung nicht
  const canSeeFinancials = currentUser && (currentUser.role === "owner" || currentUser.role === "admin");
  const allPages = {
    // Admin pages
    "admin-dashboard": <AdminDashboardPage />,
    "admin-salons": <AdminSalonsPage />,
    "admin-onboarding": <AdminOnboardingPage />,  /* ONBOARDING_VIEW_v1 */
    "admin-monitor": <AdminMonitorPage />,
    "admin-costs": <AdminCostsPage />,
    "admin-pricing": <AdminPricingPage />,
    "admin-audit": <AdminAuditPage />,  /* AUDIT_LOG_UI_v1 */
    "admin-frontend-errors": <AdminFrontendErrorsPage />,  /* FRONTEND_ERROR_LOG_v1 */
    "admin-subscriptions": <AdminSubscriptionsPage currentUser={currentUser} />,
      "subscription": <SubscriptionsPage currentUser={currentUser} />,
    "admin-updates": <AdminUpdatesPage />,
    // Salon pages
    dashboard: <DashboardPage appointments={appointments} customers={customers} employees={employees} services={services} canSeeFinancials={canSeeFinancials} enabledFeatures={enabledFeatures} onGoToPayments={() => handlePageChange("payments")} />, // NO_RECEIPT_CHECK_v1: +onGoToPayments
    calendar: <CalendarPage onGoToShifts={() => handlePageChange("shifts")} appointments={appointments} setAppointments={guardedSetAppointments} employees={employees} setEmployees={guardedSetEmployees} customers={customers} setCustomers={guardedSetCustomers} services={services} badgeAwards={badgeAwards} enabledFeatures={enabledFeatures} currentUser={currentUser} />, // POS_CHECKOUT_DEEPLINK_v1: +enabledFeatures · PAUSE_DAY_v1: +currentUser
    bookings: <BookingsPage appointments={appointments} setAppointments={guardedSetAppointments} customers={customers} employees={employees} services={services} />,
    customers: <CustomersPage customers={customers} setCustomers={guardedSetCustomers} appointments={appointments} employees={employees} services={services} setAppointments={guardedSetAppointments} currentUser={currentUser} enabledFeatures={enabledFeatures} focusCustomerId={crmFocusCustomerId} onFocusConsumed={() => setCrmFocusCustomerId(null)} />,
    services: <ServicesPage services={services} setServices={guardedSetServices} />,
    employees: <EmployeesPage employees={employees} setEmployees={guardedSetEmployees} appointments={appointments} services={services} badgeAwards={badgeAwards} onGoToSubscription={() => handlePageChange("subscription")} enabledFeatures={enabledFeatures} />, // KASSE_EMP_ACCESS_v1: +enabledFeatures
    "ai-calls": <AICallsPage />,
    messages: <MessagesPage customers={customers} onGoToSubscription={() => handlePageChange("subscription")} />,  /* SMS_TRIAL_LOCK_v1 */
    payments: <PaymentsPage appointments={appointments} setAppointments={guardedSetAppointments} customers={customers} services={services} enabledFeatures={enabledFeatures} />, // NO_RECEIPT_CHECK_v1: +enabledFeatures
    pos: <PosSystemPage />,
    kasse: <KassePage />,  /* POS_SHEARLY_SSO_v1 */
    accounting: <AccountingPage appointments={appointments} customers={customers} />,
    google: <GoogleBusinessPage />,
    "online-booking": <OnlineBookingPage services={services} employees={employees} />,
    shifts: <ShiftsPage employees={employees} currentUser={currentUser} />,  /* SALON_CLOSURE_v1: Rolle fuer Betriebsurlaub-Knopf */
    /* TIME_TRACKING_v1 -- Zeiterfassung Mitarbeiter */
    "time-tracking": <TimeTrackingPage currentUser={currentUser} employees={employees} />,
    vouchers: <VouchersPage />,
    settings: <SettingsPage />,
    help: <HelpPage />,
    dsgvo: <DSGVOPage />,
    datenschutz: <DatenschutzPage />,
    agb: <AGBPage />,
    avv: <AVVPage />,
    impressum: <ImpressumPage />,
      "profile": <ProfilePage currentUser={currentUser} />,
    /* TAX_CONSULTANT_v1 -- Steuerberater-Dashboard */
    "tax-dashboard": <TaxDashboardPage currentUser={currentUser} />,
  };

  // Only render pages the user is allowed to see (custom pages for any employee with manager_pages)
  const hasCustomPages = currentUser.role === "employee" && Array.isArray(currentUser.manager_pages) && currentUser.manager_pages.length > 0 && currentUser.manager_pages.length < 30 && currentUser.manager_pages.every(p => typeof p === "string" && p.length < 50);
  const allowedPages = hasCustomPages
    ? [...(currentUser.manager_pages || []), "profile", "help"]
    : (ROLE_PAGES[currentUser.role] || []);
  const currentPage = allowedPages.includes(activePage) ? activePage : allowedPages[0];

  // Auto-collapse sidebar nur auf Kalender (mehr Platz). Andere Seiten zeigen das Menü.
  // SIDEBAR_AUTOCOLLAPSE_CALENDAR_ONLY_v1: vorher auch "shifts" → Toby will Menü überall außer Kalender offen.
  const calendarPages = ["calendar"];
  // NOTIF_DEEP_LINK_v1 -- handlePageChange akzeptiert Compound-Format
  // "calendar:YYYY-MM-DD:apptId" → setActivePage("calendar") + Custom-Event an Kalender
  const handlePageChange = (page) => {
    let target = page;
    let focusDate = null;
    let focusApptId = null;
    let dsgvoTab = null;
    if (typeof page === "string" && page.startsWith("calendar:")) {
      const parts = page.split(":");
      target = "calendar";
      focusDate = parts[1] || null;
      focusApptId = parts[2] ? parseInt(parts[2], 10) : null;
    } else if (typeof page === "string" && page.startsWith("dsgvo:")) {
      // WIDERRUF_PREPAY_v1 -- "dsgvo:widerrufe" aus Benachrichtigung/Fristerinnerung
      target = "dsgvo";
      dsgvoTab = page.split(":")[1] || null;
    }
    setActivePage(target);
    // SIDEBAR_AUTOCOLLAPSE_CALENDAR_ONLY_v1:
    // - Klick auf Kalender: Menü zuklappen für mehr Platz
    // - Klick auf alles andere: Menü wieder ausklappen (auch wenn vorher zu war)
    if (!isMobile) {
      if (target === "calendar") setCollapsed(true);
      else setCollapsed(false);
    }
    if (focusDate || focusApptId) {
      // Verzögerung damit CalendarPage erst mountet wenn target = "calendar"
      setTimeout(() => {
        try {
          window.dispatchEvent(new CustomEvent("shearly-focus-appt", {
            detail: { date: focusDate, appointmentId: focusApptId }
          }));
        } catch (e) {}
      }, 60);
    }
    if (dsgvoTab) {
      // gleiche Verzögerung: DSGVOPage muss erst mounten
      setTimeout(() => {
        try { window.dispatchEvent(new CustomEvent("shearly-dsgvo-tab", { detail: { tab: dsgvoTab } })); } catch (e) {}
      }, 60);
    }
  };

  return (
    <DemoModeContext.Provider value={demoMode}>
    <NavigateContext.Provider value={handlePageChange}>
    <MobileMenuContext.Provider value={() => setMobileMenuOpen(true)}>
    <SpotlightContext.Provider value={() => setSpotlightOpen(true)}>{/* SPOTLIGHT_v1.2 */}
    <UserContext.Provider value={currentUser}>{/* HEADER_AVATAR_v1 */}
    <div style={{ display: "flex", flexDirection: "column", fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", minHeight: "100vh", background: COLORS.bg }}>
      {/* Demo Banner — DEMO_BANNER_PROMINENT_v1: kräftig orange/warning, "ABMELDEN"-Button damit Owner versehentlich nicht das eigene mit dem Demo verwechselt */}
      {demoMode && (
        <div style={{ background: "linear-gradient(90deg, #F59E0B, #D97706)", color: "white", textAlign: "center", padding: "12px 20px", fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 12, flexShrink: 0, boxShadow: "0 2px 12px rgba(245,158,11,0.5)", letterSpacing: 0.3, zIndex: 1000, position: "relative" }}>
          <Eye size={18} />
          <span>🎬 DEMO-MODUS · Du siehst BEISPIELDATEN — nicht dein echter Salon!</span>
          <button onClick={handleLogout} style={{ marginLeft: 8, background: "white", color: "#92400E", border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 800, letterSpacing: 0.5 }}>← ZURÜCK ZUM LOGIN</button>
        </div>
      )}
      {/* Demo Toast */}
      {demoToast && (
        <div style={{ position: "fixed", top: demoMode ? 52 : 16, left: "50%", transform: "translateX(-50%)", background: "#1F2937", color: "white", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 9999, boxShadow: "0 4px 12px rgba(0,0,0,0.3)", animation: "fadeIn 0.2s" }}>
          {demoToast}
        </div>
      )}
      {/* MAINT_NOTICE_v1 -- angekündigte Server-Wartung: für alle Rollen, nicht wegklickbar, schiebt die App
          nach unten (wie TRIAL_LOCK_v1). Verschwindet, sobald der Server keinen Hinweis mehr meldet. */}
      {sysNotice && (
        <div role="status" style={{
          background: "linear-gradient(90deg, #F59E0B, #D97706)", color: "white", textAlign: "center",
          padding: isMobile ? "calc(10px + env(safe-area-inset-top, 0px)) 16px 10px" : "10px 16px",
          fontSize: 13.5, fontWeight: 700, lineHeight: 1.45, flexShrink: 0, zIndex: 1000, position: "relative",
          boxShadow: "0 2px 12px rgba(245,158,11,0.45)"
        }}>{sysNotice}</div>
      )}
      {/* TRIAL_LOCK_v1 -- Trial-Banner für Inhaber: gelb ab 7 Tage vor Ablauf,
          ROT in der Kulanzfrist. Täglich sichtbar (kein Wegklicken), Link "Mein Abo". */}
      {currentUser.role === "owner" && currentUser.salon_lock &&
        (currentUser.salon_lock.state === "warning" || currentUser.salon_lock.state === "grace") && (
        <div style={{
          background: currentUser.salon_lock.state === "grace"
            ? "linear-gradient(90deg, #DC2626, #B91C1C)"
            : "linear-gradient(90deg, #F59E0B, #D97706)",
          color: "white", textAlign: "center", padding: "10px 16px", fontSize: 13.5,
          fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center",
          gap: 12, flexWrap: "wrap", flexShrink: 0, zIndex: 1000, position: "relative",
          boxShadow: currentUser.salon_lock.state === "grace"
            ? "0 2px 12px rgba(220,38,38,0.45)" : "0 2px 12px rgba(245,158,11,0.45)"
        }}>
          <span>
            {currentUser.salon_lock.state === "grace"
              ? `🔴 Dein Testzeitraum ist abgelaufen — noch ${currentUser.salon_lock.grace_days_left} ${currentUser.salon_lock.grace_days_left === 1 ? "Tag" : "Tage"} Kulanz, danach wird der Zugang gesperrt.`
              : (currentUser.salon_lock.days_left === 0
                ? "⏳ Dein Testzeitraum endet heute."
                : `⏳ Dein Testzeitraum endet in ${currentUser.salon_lock.days_left} ${currentUser.salon_lock.days_left === 1 ? "Tag" : "Tagen"}${currentUser.salon_lock.trial_ends_at ? " (am " + new Date(currentUser.salon_lock.trial_ends_at).toLocaleDateString("de-DE") + ")" : ""}.`)}
          </span>
          <button onClick={() => handlePageChange("subscription")} style={{
            background: "white", color: currentUser.salon_lock.state === "grace" ? "#991B1B" : "#92400E",
            border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12,
            cursor: "pointer", fontWeight: 800, letterSpacing: 0.3
          }}>Jetzt Plan wählen → Mein Abo</button>
        </div>
      )}
      {/* PAYMENT_DUNNING_v1 -- gelbes, wegklickbares Banner bei Zahl-Fehlversuch(en),
          solange noch NICHT gesperrt (Fehlversuch 1-2). Stripe wiederholt automatisch. */}
      {currentUser.role === "owner" && !readOnly && currentUser.salon_lock &&
        (currentUser.salon_lock.payment_failed_count || 0) >= 1 && !payBannerDismissed && (
        <div style={{
          background: "linear-gradient(90deg, #F59E0B, #D97706)", color: "white",
          textAlign: "center", padding: "10px 16px", fontSize: 13.5, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
          flexWrap: "wrap", flexShrink: 0, zIndex: 1000, position: "relative",
          boxShadow: "0 2px 12px rgba(245,158,11,0.45)"
        }}>
          <span>💳 Deine letzte Zahlung konnte nicht abgebucht werden — Stripe versucht es automatisch erneut. Bitte prüfe deine Zahlungsdaten.</span>
          <button onClick={() => handlePageChange("subscription")} style={{
            background: "white", color: "#92400E", border: "none", borderRadius: 6,
            padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 800, letterSpacing: 0.3
          }}>Zahlung prüfen → Mein Abo</button>
          <button onClick={() => setPayBannerDismissed(true)} title="Ausblenden" style={{
            background: "rgba(255,255,255,0.25)", color: "white", border: "none", borderRadius: 6,
            padding: "6px 10px", fontSize: 12, cursor: "pointer", fontWeight: 800
          }}>✕</button>
        </div>
      )}
      {/* TRIAL_READONLY_v1 -- schlanker Hinweisbalken im Lese-Modus.
          PAYMENT_DUNNING_v1 -- Text je nach Sperr-Grund (Trial vs. Zahlungsverzug). */}
      {readOnly && (
        <div style={{
          background: "linear-gradient(90deg, #DC2626, #B91C1C)", color: "white",
          textAlign: "center", padding: "10px 16px", fontSize: 13.5, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
          flexWrap: "wrap", flexShrink: 0, zIndex: 1000, position: "relative",
          boxShadow: "0 2px 12px rgba(220,38,38,0.45)"
        }}>
          <span>{currentUser.salon_lock && currentUser.salon_lock.reason === "payment_overdue"
            ? "🔒 Zahlung offen — Nur-Lese-Modus. Deine Daten & Termine sind sicher; bitte Zahlung im Abo-Bereich klären."
            : "🔒 Testzeitraum abgelaufen — Nur-Lese-Modus. Deine Daten & Termine sind sicher; zum Bearbeiten bitte ein Abo aktivieren."}</span>
          <button onClick={() => handlePageChange("subscription")} style={{
            background: "white", color: "#991B1B", border: "none", borderRadius: 6,
            padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 800, letterSpacing: 0.3
          }}>{currentUser.salon_lock && currentUser.salon_lock.reason === "payment_overdue" ? "Zahlung klären → Mein Abo" : "Jetzt Abo aktivieren → Mein Abo"}</button>
        </div>
      )}
      {/* TRIAL_READONLY_v1 -- Hinweisfenster bei Login (1x pro Login) */}
      {readOnly && showExpiredModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px" }}
          onClick={() => setShowExpiredModal(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, width: "100%", background: "var(--card)", border: `1.5px solid ${COLORS.border}`, borderRadius: 16, padding: "30px 26px", boxShadow: "0 12px 40px rgba(0,0,0,0.3)", textAlign: "center" }}>
            <div style={{ fontSize: 42, marginBottom: 10 }}>🔒</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.text, marginBottom: 12 }}>{currentUser.salon_lock && currentUser.salon_lock.reason === "payment_overdue" ? "Zahlung offen" : "Dein Testzeitraum ist abgelaufen"}</div>
            <div style={{ fontSize: 14.5, lineHeight: 1.6, color: COLORS.text, opacity: 0.92, marginBottom: 16 }}>
              {currentUser.salon_lock && currentUser.salon_lock.reason === "payment_overdue"
                ? <>Deine letzten Abbuchungen sind fehlgeschlagen. Du kannst dein System weiter ansehen, aber <strong>vorerst nichts mehr ändern</strong>. <strong>Alle deine Termine, Kunden und Einstellungen bleiben vollständig erhalten</strong> — sobald die Zahlung geklärt ist, geht es sofort genau dort weiter, wo du aufgehört hast.</>
                : <>Du kannst dein System weiter ansehen, aber <strong>vorerst nichts mehr ändern</strong>. <strong>Alle deine Termine, Kunden und Einstellungen bleiben vollständig erhalten</strong> — sobald du ein Abo aktivierst, geht es sofort genau dort weiter, wo du aufgehört hast.</>}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Btn onClick={() => { setShowExpiredModal(false); handlePageChange("subscription"); }}>Zu den Abos →</Btn>
              <button onClick={() => setShowExpiredModal(false)} style={{ background: "transparent", border: "none", color: "var(--textLight)", fontSize: 13, cursor: "pointer", fontWeight: 600 }}>Später ansehen</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <Sidebar active={currentPage} setActive={handlePageChange} collapsed={collapsed} setCollapsed={setCollapsed} currentUser={currentUser} onLogout={handleLogout} mobileOpen={mobileMenuOpen} setMobileOpen={setMobileMenuOpen} enabledFeatures={enabledFeatures} />
        <main
          onScroll={(e) => { try { const v = isMobile && e.currentTarget.scrollTop > 72; setShowMobileFab(prev => prev === v ? prev : v); } catch(_){} }}
          /* SAFE_AREA_TOP_v1 (Toby 22.09.2026, iPhone 14 Pro Homescreen: Menue-Striche lagen unter Statusleiste/Dynamic Island) */
          style={{ flex: 1, padding: isMobile ? "calc(16px + env(safe-area-inset-top, 0px)) 16px 16px" : (collapsed && calendarPages.includes(currentPage)) ? "16px 16px" : 24, overflow: "auto", maxHeight: "100vh", width: isMobile ? "100%" : "auto" }}>
          {allPages[currentPage]}
        </main>
      </div>
      {/* MOBILE_MENU_FAB_v1 (#16) -- Header (mit Hamburger) scrollt weg -> fester Menue-Button
          erscheint beim Scrollen, damit man auf Mobile immer zur Navigation zurueck kommt. */}
      {isMobile && showMobileFab && !mobileMenuOpen && (
        <button onClick={() => setMobileMenuOpen(true)} aria-label="Menü öffnen" title="Menü"
          style={{
            position: "fixed", top: "calc(10px + env(safe-area-inset-top, 0px))", left: 10, zIndex: 1500, /* SAFE_AREA_TOP_v1 */
            width: 42, height: 42, borderRadius: 10,
            background: COLORS.card, border: `1px solid ${COLORS.border}`,
            boxShadow: "0 2px 10px rgba(0,0,0,0.20)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", padding: 0,
          }}>
          <Menu size={20} color={COLORS.text} />
        </button>
      )}
      {/* SPOTLIGHT_v1 -- Cmd+K Global-Suche */}
      <SpotlightSearch
        open={spotlightOpen}
        onClose={() => setSpotlightOpen(false)}
        customers={customers}
        appointments={appointments}
        employees={employees}
        services={services}
        onOpenCustomer={setCrmFocusCustomerId}
      />
      {/* FUN_v1 -- Konfetti-Overlay bei Online-Buchungs-Meilensteinen */}
      <ConfettiOverlay milestone={celebrate} onClose={() => setCelebrate(null)} />
      {/* WHATS_NEW_v1 -- Update-Popup nach Deploy */}
      <WhatsNewModal updates={unseenUpdates} onDismiss={dismissUpdates} />
      {/* FIRST_LOGIN_GREETING_v1 -- Begrüßungsfenster erster Anmelder des Tages */}
      {showGreeting && <FirstLoginGreeting
        userName={currentUser && currentUser.name}
        todayCount={(appointments || []).filter(a => a && a.date === formatDate(today) && a.status !== "cancelled" && !["blocker", "vacation_auto", "phase", "assistant"].includes(a.source)).length}
        onDismiss={() => setShowGreeting(false)} />}
      {/* FUN_v1 -- Retro-Mode-Hinweis (Konami-Code) */}
      {retroMode && (
        <div style={{
          position: "fixed", bottom: 20, left: 20, zIndex: 100001,
          background: "linear-gradient(135deg, #7C3AED, #EC4899)", color: "white",
          padding: "10px 18px", borderRadius: 10, fontSize: 12, fontWeight: 700,
          boxShadow: "0 4px 20px rgba(124,58,237,0.5)",
          letterSpacing: 1, fontFamily: "monospace",
        }}>
          🎮 RETRO-MODE AKTIVIERT · 60s · KONAMI
          <button onClick={() => setRetroMode(false)} style={{
            marginLeft: 12, background: "rgba(255,255,255,0.2)", color: "white",
            border: "none", borderRadius: 4, padding: "2px 8px", cursor: "pointer",
            fontSize: 11, fontWeight: 700,
          }}>×</button>
        </div>
      )}
    </div>
    </UserContext.Provider>
    </SpotlightContext.Provider>
    </MobileMenuContext.Provider>
    </NavigateContext.Provider>
    </DemoModeContext.Provider>
  );
}


    try { window.api = api; } catch(e) {} // IMPERSONATION_UI_v1 expose
    ReactDOM.createRoot(document.getElementById("root")).render(  // OWNER_PIN_v1: PinResetGate ueber allem
      React.createElement(React.Fragment, null, React.createElement(App), React.createElement(PinResetGate)));
